import { apiRequest } from "@common/api";
import type { GamePlay } from "./playtime";

// The games-and-players feed from play-api's /dashboard.

export interface GameSaves {
  /** The ROM's base name. */
  game: string;
  lastSave: string;
  hasBatterySave: boolean;
  states: number;
}

export interface UserSummary {
  sub: string;
  email: string | null;
  /** The account's display name from Cognito, when set. */
  name: string | null;
  lastSave: string | null;
  /** Games with cloud saves. */
  games: GameSaves[];
  /** Play time per game, most recent first. */
  played: GamePlay[];
  secondsPlayed: number;
  lastPlayed: string | null;
  /** The game whose cover is their picture, by play key; null until they choose. */
  picture: string | null;
}

export interface GamePlayer {
  sub: string;
  email: string | null;
  name: string | null;
  seconds: number;
  lastPlayed: string;
}

export interface GameSummary {
  game: string;
  name: string;
  system: string;
  seconds: number;
  lastPlayed: string;
  players: GamePlayer[];
}

export interface Feed {
  players: UserSummary[];
  games: GameSummary[];
}

export async function getDashboard(): Promise<Feed> {
  return (await apiRequest("/dashboard")).json();
}

/** How a player is shown: their display name, else the part of their email before the @, else their account id. */
export function playerName(player: { sub: string; email: string | null; name?: string | null }): string {
  return player.name || (player.email ?? `Account ${player.sub.slice(0, 8)}`).split("@")[0];
}

/** The games with the most time on them across every player, longest first. */
export function topGames(feed: Feed, count: number): GameSummary[] {
  return feed.games
    .filter((game) => game.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, count);
}

/** The players with the most time played, longest first. */
export function topPlayers(feed: Feed, count: number): UserSummary[] {
  return feed.players
    .filter((player) => player.secondsPlayed > 0)
    .sort((a, b) => b.secondsPlayed - a.secondsPlayed)
    .slice(0, count);
}

/** A player's most recent activity, play or save. */
export function lastActive(user: UserSummary): string | null {
  return [user.lastPlayed, user.lastSave].filter(Boolean).sort().pop() ?? null;
}

/** "4 min ago", "2 h ago", "3 d ago", or a date past a month. */
export function ago(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days < 30 ? `${days} d ago` : new Date(iso).toLocaleDateString();
}
